import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter, Stack } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { useScoreHistory } from "@/modules/scores";

interface UserData {
  id: string;
  full_name: string | null;
  nickname: string | null;
  avatar_url: string | null;
}

interface TeamInfo {
  id: string;
  name: string;
  color: string | null;
}

/**
 * User Score Detail Screen
 * Shows user info, their team, and score contributions
 */
export default function UserScoreDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const colors = useThemeColors();

  const [user, setUser] = useState<UserData | null>(null);
  const [team, setTeam] = useState<TeamInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const {
    history,
    summaryByType,
    totalPoints,
    loading: historyLoading,
    refresh: refreshHistory,
  } = useScoreHistory({ userId: id, limit: 100 });

  /**
   * Fetch user data and team info
   */
  const fetchUserData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Fetch user profile
      const { data: userData, error: userError } = await supabase
        .from("profiles")
        .select("id, full_name, nickname, avatar_url")
        .eq("id", id)
        .single();

      if (userError) throw userError;
      setUser(userData);

      // Fetch user's team membership
      const { data: membershipData, error: membershipError } = await supabase
        .from("team_members")
        .select(`
          team_id,
          teams:team_id (
            id,
            name,
            color
          )
        `)
        .eq("user_id", id)
        .limit(1)
        .single();

      if (!membershipError && membershipData?.teams) {
        setTeam({
          id: (membershipData.teams as any).id,
          name: (membershipData.teams as any).name,
          color: (membershipData.teams as any).color,
        });
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchUserData(), refreshHistory()]);
    setRefreshing(false);
  }, [fetchUserData, refreshHistory]);

  /**
   * Get initials from name
   */
  const getInitials = (name: string | null): string => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Get icon for game type
   */
  const getGameTypeIcon = (
    gameType: string
  ): keyof typeof Ionicons.glyphMap => {
    switch (gameType.toLowerCase()) {
      case "quiz":
        return "help-circle-outline";
      case "speurtocht":
        return "search-outline";
      case "challenge":
      case "uitdaging":
        return "flash-outline";
      case "photo":
      case "foto":
        return "camera-outline";
      case "boter_kaas_eieren":
      case "tic_tac_toe":
        return "grid-outline";
      case "puzzle_matching":
      case "memory":
        return "images-outline";
      case "chess":
      case "schaken":
        return "extension-puzzle-outline";
      case "endless_runner":
        return "walk-outline";
      case "sudoku":
        return "grid-outline";
      case "match_3":
        return "diamond-outline";
      case "dammen":
        return "ellipse-outline";
      case "manual":
        return "hand-left-outline";
      default:
        return "trophy-outline";
    }
  };

  const displayName = user?.nickname || user?.full_name || "Onbekend";

  if (loading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <Text style={{ color: colors.textSecondary }}>Laden...</Text>
      </SafeAreaView>
    );
  }

  if (!user) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <Ionicons name="alert-circle-outline" size={48} color={colors.error} />
        <Text className="mt-4" style={{ color: colors.text }}>
          Gebruiker niet gevonden
        </Text>
      </SafeAreaView>
    );
  }

  // Calculate max points for progress bar scaling
  const maxTypePoints = Math.max(...summaryByType.map((s) => s.total_points), 1);

  return (
    <>
      <Stack.Screen options={{ title: displayName }} />
      <SafeAreaView
        className="flex-1"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
        >
          {/* User Header */}
          <View
            className="mx-4 mt-4 rounded-2xl p-6 items-center"
            style={{ backgroundColor: colors.surface }}
          >
            <View
              className="w-24 h-24 rounded-full items-center justify-center mb-4 overflow-hidden"
              style={{ backgroundColor: colors.primaryLight }}
            >
              {user.avatar_url ? (
                <Image
                  source={{ uri: user.avatar_url }}
                  className="w-full h-full"
                  resizeMode="cover"
                />
              ) : (
                <Text
                  className="text-4xl font-bold"
                  style={{ color: colors.primary }}
                >
                  {getInitials(user.nickname || user.full_name)}
                </Text>
              )}
            </View>
            <Text
              className="text-2xl font-bold"
              style={{ color: colors.text }}
            >
              {displayName}
            </Text>
            {user.full_name && user.nickname && (
              <Text
                className="mt-1"
                style={{ color: colors.textSecondary }}
              >
                {user.full_name}
              </Text>
            )}

            {/* Team Badge */}
            {team && (
              <TouchableOpacity
                className="flex-row items-center mt-3 px-4 py-2 rounded-full"
                style={{ backgroundColor: team.color || colors.primaryLight }}
                activeOpacity={0.7}
                onPress={() => router.push(`/scores/team/${team.id}`)}
              >
                <View
                  className="w-3 h-3 rounded-full mr-2"
                  style={{
                    backgroundColor: team.color
                      ? "rgba(255,255,255,0.3)"
                      : colors.primary,
                  }}
                />
                <Text
                  className="font-medium"
                  style={{ color: team.color ? "#FFFFFF" : colors.primary }}
                >
                  {team.name}
                </Text>
                <Ionicons
                  name="chevron-forward"
                  size={16}
                  color={team.color ? "#FFFFFF" : colors.primary}
                  style={{ marginLeft: 4 }}
                />
              </TouchableOpacity>
            )}

            {/* Total Points */}
            <View className="flex-row items-center mt-6">
              <Ionicons name="star" size={28} color={colors.warning} />
              <Text
                className="text-3xl font-bold ml-2"
                style={{ color: colors.primary }}
              >
                {totalPoints.toLocaleString()}
              </Text>
              <Text
                className="ml-2"
                style={{ color: colors.textSecondary }}
              >
                punten bijgedragen
              </Text>
            </View>
          </View>

          {/* Score Breakdown by Type */}
          {summaryByType.length > 0 && (
            <View className="mx-4 mt-6">
              <Text
                className="text-lg font-bold mb-3"
                style={{ color: colors.text }}
              >
                Punten per Categorie
              </Text>
              <View
                className="rounded-xl p-4"
                style={{ backgroundColor: colors.surface }}
              >
                {summaryByType.map((item, index) => (
                  <View
                    key={item.game_type}
                    className={index > 0 ? "mt-4" : ""}
                  >
                    <View className="flex-row items-center justify-between mb-2">
                      <View className="flex-row items-center">
                        <Ionicons
                          name={getGameTypeIcon(item.game_type)}
                          size={18}
                          color={colors.primary}
                        />
                        <Text
                          className="ml-2 font-medium"
                          style={{ color: colors.text }}
                        >
                          {item.game_type}
                        </Text>
                      </View>
                      <View className="flex-row items-center">
                        <Text
                          className="font-semibold"
                          style={{ color: colors.primary }}
                        >
                          {item.total_points.toLocaleString()}
                        </Text>
                        <Text
                          className="text-sm ml-1"
                          style={{ color: colors.textTertiary }}
                        >
                          ({item.entries_count}x)
                        </Text>
                      </View>
                    </View>
                    <View
                      className="h-2 rounded-full overflow-hidden"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <View
                        className="h-full rounded-full"
                        style={{
                          backgroundColor: colors.primary,
                          width: `${(item.total_points / maxTypePoints) * 100}%`,
                        }}
                      />
                    </View>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Score History */}
          <View className="mx-4 mt-6 mb-8">
            <Text
              className="text-lg font-bold mb-3"
              style={{ color: colors.text }}
            >
              Bijdrage Geschiedenis
            </Text>
            {history.length === 0 ? (
              <View
                className="rounded-xl p-6 items-center"
                style={{ backgroundColor: colors.surface }}
              >
                <Ionicons
                  name="time-outline"
                  size={32}
                  color={colors.textTertiary}
                />
                <Text
                  className="mt-2 text-center"
                  style={{ color: colors.textSecondary }}
                >
                  Nog geen punten verdiend
                </Text>
                <Text
                  className="mt-1 text-center text-sm px-4"
                  style={{ color: colors.textTertiary }}
                >
                  Doe mee aan uitdagingen en games om punten te verdienen!
                </Text>
              </View>
            ) : (
              <View
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: colors.surface }}
              >
                {history.map((entry, index) => (
                  <View
                    key={entry.id}
                    className="flex-row items-center p-4"
                    style={{
                      borderTopWidth: index > 0 ? 1 : 0,
                      borderTopColor: colors.border,
                    }}
                  >
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{
                        backgroundColor:
                          entry.points >= 0
                            ? colors.successLight
                            : colors.errorLight,
                      }}
                    >
                      <Ionicons
                        name={entry.points >= 0 ? "add" : "remove"}
                        size={20}
                        color={
                          entry.points >= 0 ? colors.success : colors.error
                        }
                      />
                    </View>
                    <View className="flex-1">
                      <Text
                        className="font-medium"
                        style={{ color: colors.text }}
                        numberOfLines={1}
                      >
                        {entry.reason}
                      </Text>
                      <View className="flex-row items-center mt-0.5">
                        <Text
                          className="text-sm"
                          style={{ color: colors.textSecondary }}
                        >
                          {formatDate(entry.created_at)}
                        </Text>
                        {entry.team_name && (
                          <View className="flex-row items-center ml-2">
                            <View
                              className="w-2 h-2 rounded-full mr-1"
                              style={{
                                backgroundColor:
                                  entry.team_color || colors.primary,
                              }}
                            />
                            <Text
                              className="text-sm"
                              style={{ color: colors.textTertiary }}
                            >
                              {entry.team_name}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>
                    <Text
                      className="font-bold text-lg"
                      style={{
                        color:
                          entry.points >= 0 ? colors.success : colors.error,
                      }}
                    >
                      {entry.points >= 0 ? "+" : ""}
                      {entry.points}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </>
  );
}

